import {
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ActivityFileTypeEnum } from '@sport-activities/sdk';
import { Subscription } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { appConfig } from '~/app.config';

import { CONNECT_ISSUER } from '~/auth/providers/connect.provider';
import { AuthService } from '~/auth/services/auth/auth.service';
import { UserService } from '~/auth/services/user/user.service';
import { BackLinkInterface } from '~/checkout/interfaces/back-link.interface';
import { CheckoutItem } from '~/checkout/model/checkout-item.model';
import { BookingService } from '~/checkout/services/booking/booking.service';
import { CheckoutService } from '~/checkout/services/checkout/checkout.service';
import { VtmnPlayButtonSizeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-size.enum';
import { CtaButtonTypeEnum } from '~/component/call-to-action/enums/button-type.enum';
import { CheckboxAlignEnum } from '~/component/form/checkbox/enums/checkbox-align.enum';
import { formatIsoDuration } from '~/core/helpers/duration-formatter/duration-formatter.helper';
import { getRefundTimeLimit } from '~/core/helpers/refund-time-limit/refund-time-limit.helper';
import { urlToBackLink } from '~/core/helpers/url/url-to-back-link/url-to-back-link.helper';
import { Activity } from '~/core/model/activity.model';
import { LoaderService } from '~/core/services/loader/loader.service';
import { RedirectionService } from '~/core/services/redirection/redirection.service';
import { RouterHistoryService } from '~/core/services/router-history/router-history.service';
import { TitleService } from '~/core/services/title/title.service';
import { LocaleService } from '~/i18n/services/locale.service';
import { ActivityMediaResolutionEnum } from '~/organizer/shared/enums/media-resolution.enum';
import { ParamethorService } from '~/paramethor/paramethor.service';
import { ParamethorNameEnum } from '~/paramethor/names.enum';

@Component({
  selector: 'app-summary',
  templateUrl: './summary.component.html',
})
export class SummaryComponent implements OnInit, OnDestroy {
  public readonly checkboxAlign = CheckboxAlignEnum.Top;
  public readonly subscriptionLoaderKey = 'SUBSCRIPTION_LOADER_KEY';
  public readonly ctaButtonType = CtaButtonTypeEnum;
  public readonly vtmnPlayButtonSize = VtmnPlayButtonSizeEnum;

  private bookingStartSubscription: Subscription;
  private isLoggedIn: boolean;

  public ctaSubmit = 'checkout.summary.confirmation-pay';
  public items: CheckoutItem[];
  public summaryForm: FormGroup;

  constructor(
    private readonly checkoutService: CheckoutService,
    public appSummaryElementRef: ElementRef,
    private readonly formBuilder: FormBuilder,
    private readonly bookingService: BookingService,
    private readonly userService: UserService,
    private readonly authService: AuthService,
    private readonly translateService: TranslateService,
    private readonly redirectionService: RedirectionService,
    private readonly router: Router,
    private readonly titleService: TitleService,
    private readonly routerHistory: RouterHistoryService,
    private readonly paramethorService: ParamethorService,
    private readonly localeService: LocaleService,
    @Inject(CONNECT_ISSUER) private readonly connectIssuer: string,
    private readonly loaderService: LoaderService
  ) {}

  public ngOnDestroy(): void {
    if (this.bookingStartSubscription) {
      this.bookingStartSubscription.unsubscribe();
    }
  }

  public ngOnInit(): void {
    this.titleService.setPageTitle('seo.checkout.summary.title');

    this.summaryForm = this.formBuilder.group({
      termsOfService: [false, Validators.requiredTrue],
      allowExternalPaymentChecker: [
        false,
        this.showExternalPayment ? Validators.requiredTrue : null,
      ],
    });

    setTimeout(() => {
      this.checkoutService.getItems().subscribe((checkoutItems) => {
        this.items = checkoutItems.map(
          (checkoutItem) => new CheckoutItem(checkoutItem)
        );
        if (this.checkoutService.isFreeCheckout()) {
          this.ctaSubmit = 'checkout.summary.confirmation-free';
        }
      });

      // Check if user is logged to display cta text
      this.userService.isAuthenticated().subscribe((isLoggedIn) => {
        this.isLoggedIn = isLoggedIn;

        if (isLoggedIn) {
          return;
        }

        this.ctaSubmit = 'checkout.summary.confirmation-login';
      });

      if (this.showExternalPayment) {
        this.ctaSubmit = 'checkout.summary.confirmation-external-payment';
      }
    }, 0);
  }

  public get backLink(): BackLinkInterface {
    return urlToBackLink(this.routerHistory.previous || '/');
  }

  public get getFormattedRefundTime(): string {
    return formatIsoDuration(
      getRefundTimeLimit(this.items[0]?.activity, appConfig.refundLimit),
      this.localeService.dateLocale,
      true
    );
  }

  public get isFreeCheckoutAndNotLogged(): boolean {
    return this.checkoutService.isFreeCheckout() && !this.isLoggedIn;
  }

  public get showExternalPayment(): boolean {
    return (
      this.paramethorService.isTrue(
        ParamethorNameEnum.AllowExternalPayment
      ) &&
      this.items &&
      this.items[0]?.activity?.startingPrice?.amount > 0
    );
  }

  public confirm(): void {
    this.loaderService.start(this.subscriptionLoaderKey);

    if (this.summaryForm.invalid) {
      this.summaryForm.markAllAsTouched();
      return;
    }

    // Is paying booking
    if (!this.showExternalPayment && !this.checkoutService.isFreeCheckout()) {
      this.loaderService.stop(this.subscriptionLoaderKey);
      // User need to pay, so redirect on billing address page
      this.redirectionService.redirect('/checkout.billing-address');
      return;
    }

    if (!this.isLoggedIn) {
      this.loaderService.stop(this.subscriptionLoaderKey);
      return this.login();
    }

    // Is free booking and connected
    this.bookingStartSubscription = this.bookingService
      // 1 - Booking Start
      .start(this.items)
      .pipe(
        // 2 - Booking confirm
        mergeMap((bookingResponse) => {
          return this.bookingService.confirm(bookingResponse.bookingId);
        }),
        // 3 - Redirect
        map(() => {
          this.loaderService.stop(this.subscriptionLoaderKey);
          return this.checkoutService.redirectToConfirmation();
        })
      )
      .subscribe();
  }

  public getPicture(activity: Activity): string {
    return (
      activity.getMediaUrlByType(
        'MAIN_MEDIA' as ActivityFileTypeEnum,
        ActivityMediaResolutionEnum.LARGE
      ) || activity.picture
    );
  }

  public login(): void {
    this.authService.login(this.connectIssuer, {
      from: this.router.url,
      lang: this.translateService.currentLang,
    });
  }
}
